import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PROTECTED_EMAIL = "accoupro@gmail.com";
const CONFIRMATION_TEXT = "RESET PLATFORM";

export function DangerZone() {
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (confirmText !== CONFIRMATION_TEXT) {
      toast.error(`Please type "${CONFIRMATION_TEXT}" to confirm`);
      return;
    }

    setResetting(true);
    try {
      // Delete all companies (cascading will handle related data)
      const { error: companiesErr } = await supabase
        .from("companies")
        .delete()
        .neq("name", "__never_match__"); // delete all

      if (companiesErr) console.error("Companies cleanup:", companiesErr);

      // Delete all non-admin profiles (protect super admin)
      // First get the super admin user ID
      const { data: adminProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", PROTECTED_EMAIL)
        .single();

      const adminId = adminProfile?.id;

      if (adminId) {
        // Reset all non-admin profiles' company_id
        const { error: profilesErr } = await supabase
          .from("profiles")
          .update({ company_id: null })
          .neq("id", adminId);

        if (profilesErr) console.error("Profiles cleanup:", profilesErr);
      }

      // Clean up other tables
      const tablesToClean = [
        "voucher_lines",
        "vouchers",
        "ledgers",
        "inventory_items",
        "customers",
        "analytics_events",
        "analytics_daily_summary",
        "announcement_reads",
        "announcements",
        "branch_financial_data",
        "branch_user_assignments",
        "branches",
        "ca_client_activities",
        "ca_company_access",
        "email_notifications",
        "marketplace_messages",
        "marketplace_transactions",
        "service_request_attachments",
        "service_proposals",
        "service_requests",
        "service_orders",
        "service_listings",
        "package_subscriptions",
        "partner_leads",
        "user_feedback",
        "feedback_votes",
        "feature_request_votes",
        "feature_requests",
        "forum_votes",
        "forum_answers",
        "forum_questions",
        "education_articles",
        "education_videos",
        "contact_messages",
      ] as const;

      for (const table of tablesToClean) {
        try {
          await supabase.from(table).delete().neq("id", "__never__");
        } catch (e) {
          console.error(`Cleanup ${table}:`, e);
        }
      }

      toast.success("Platform has been reset to clean state. Super admin account preserved.");
      setShowDialog(false);
      setConfirmText("");
    } catch (err: any) {
      toast.error(err.message || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-6 rounded-lg border-2 border-destructive/30 bg-destructive/5 space-y-4">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <div>
          <h2 className="text-lg font-bold text-destructive">Danger Zone</h2>
          <p className="text-sm text-muted-foreground">Irreversible actions. Proceed with extreme caution.</p>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-destructive/20">
        <div>
          <p className="text-sm font-medium text-foreground">Reset Entire Platform</p>
          <p className="text-xs text-muted-foreground">
            Deletes all companies, users data, invoices, transactions, inventory, reports, and logs.
            The super admin account ({PROTECTED_EMAIL}) will be preserved.
          </p>
        </div>
        <Button variant="destructive" onClick={() => setShowDialog(true)}>
          <Trash2 className="h-4 w-4 mr-1" /> Reset Platform
        </Button>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Reset Entire Platform
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete ALL data from the platform including companies, users, invoices, transactions, inventory, reports, and logs.
              <br /><br />
              <strong>The super admin account ({PROTECTED_EMAIL}) will NOT be deleted.</strong>
              <br /><br />
              Type <strong>{CONFIRMATION_TEXT}</strong> to confirm.
            </DialogDescription>
          </DialogHeader>

          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type "${CONFIRMATION_TEXT}" to confirm`}
            className="border-destructive/30 focus:ring-destructive/30"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmText !== CONFIRMATION_TEXT || resetting}
              onClick={handleReset}
            >
              {resetting ? "Resetting..." : "Confirm Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
